/**
 * Cotizaciones hechas desde el panel (COT-01).
 *
 * El negocio las armaba a mano; aca se arman con los productos y los precios de
 * la tienda y sale el PDF con el mismo formato. Se guardan los datos, no el
 * PDF: cada descarga lo arma de nuevo desde lo guardado.
 *
 * La fila abre el detalle en un modal (`?cotizacion=<id>`), como Pedidos, y
 * desde ahi se descarga, se duplica o se elimina.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { ModalCotizacion } from '../../componentes/administracion/ModalCotizacion';
import { Tabla } from '../../componentes/administracion/Tabla';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { Paginador } from '../../componentes/catalogo/Paginador';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { obtenerCotizaciones } from '../../servicios/cotizaciones.servicio';
import { PARAMETRO_COTIZACION } from '../../utilidades/cotizacion';
import { fechaHora, numero, pesos } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';

function idDe(valor: string | null): number | null {
  const id = Number(valor);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function Cotizaciones() {
  const [parametros, setParametros] = useSearchParams();
  const pagina = Number(parametros.get('pagina') ?? '1');
  const termino = parametros.get('termino') ?? '';
  const abierta = idDe(parametros.get(PARAMETRO_COTIZACION));

  // Cambia al eliminar una y obliga a pedir la lista otra vez.
  const [version, setVersion] = useState(0);
  const [aviso, setAviso] = useState<string | null>(null);

  const { estado, datos, error } = usePeticion(
    (senal) => obtenerCotizaciones({ pagina, termino: termino || undefined }, senal),
    [pagina, termino, version],
  );

  function buscar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const valor = new FormData(evento.currentTarget).get('termino');
    const texto = typeof valor === 'string' ? valor.trim() : '';
    setParametros(texto.length > 0 ? { termino: texto } : {});
  }

  function rutaCon(id: number): string {
    const siguientes = new URLSearchParams(parametros);
    siguientes.set(PARAMETRO_COTIZACION, String(id));
    return `?${siguientes.toString()}`;
  }

  function cerrar(): void {
    const siguientes = new URLSearchParams(parametros);
    siguientes.delete(PARAMETRO_COTIZACION);
    setParametros(siguientes, { replace: true });
  }

  const rutaListado =
    termino.length > 0
      ? `${rutas.administracion.cotizaciones()}?${new URLSearchParams({ termino }).toString()}`
      : rutas.administracion.cotizaciones();

  return (
    <>
      <Seo
        titulo="Cotizaciones"
        descripcion="Cotizaciones armadas para clientes."
        ruta={rutas.administracion.cotizaciones()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Cotizaciones"
        bajada={datos !== null ? `${numero(datos.total)} guardadas` : undefined}
        acciones={
          <>
            <form onSubmit={buscar} role="search">
              <label className="solo-lectores" htmlFor="buscar-cotizaciones">
                Buscar cotizaciones por numero, cliente, RUT o contacto
              </label>
              <input
                key={termino}
                id="buscar-cotizaciones"
                name="termino"
                type="search"
                className="entrada"
                placeholder="Número, cliente, RUT..."
                defaultValue={termino}
              />
              <button type="submit" className="btn btn-sm btn-contorno">
                <Icono nombre="buscar" />
                Buscar
              </button>
            </form>
            <Link className="btn btn-sm btn-primario" to={rutas.administracion.cotizacionNueva()}>
              <Icono nombre="mas" />
              Nueva cotización
            </Link>
          </>
        }
      />

      <div className="admin-cuerpo">
        {aviso !== null ? (
          <p className="acceso-aviso" role="status">
            <Icono nombre="checkCirc" />
            <span>{aviso}</span>
          </p>
        ) : null}

        {estado === 'cargando' ? <Cargando que="las cotizaciones" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null && datos.items.length === 0 ? (
          <p className="panel-vacio" role="status">
            {termino.length > 0
              ? `Ninguna cotización calza con «${termino}».`
              : 'Todavía no hay cotizaciones. Arma la primera con «Nueva cotización».'}
          </p>
        ) : null}

        {datos !== null && datos.items.length > 0 ? (
          <>
            <Tabla
              titulo="Cotizaciones guardadas"
              encabezados={
                <>
                  <th scope="col">Cotización</th>
                  <th scope="col">Cliente</th>
                  <th scope="col" className="der">
                    Total
                  </th>
                </>
              }
            >
              {datos.items.map((cotizacion) => (
                <tr
                  key={cotizacion.id}
                  className={cotizacion.id === abierta ? 'fila-enlace fila-seleccionada' : 'fila-enlace'}
                >
                  <th scope="row" className="sin-cortes">
                    <Link className="enlace-fila" to={rutaCon(cotizacion.id)} replace>
                      <span className="mono">{cotizacion.numero}</span>
                      <span className="solo-lectores">, ver el detalle</span>
                    </Link>
                    <span className="s">
                      <time dateTime={cotizacion.creadoEn}>{fechaHora(cotizacion.creadoEn)}</time>
                    </span>
                  </th>
                  <td className="partible">
                    {cotizacion.nombreCliente}
                    <span className="s">
                      {numero(cotizacion.cantidadProductos)}{' '}
                      {cotizacion.cantidadProductos === 1 ? 'producto' : 'productos'}
                    </span>
                  </td>
                  <td className="der num">{pesos(cotizacion.total)}</td>
                </tr>
              ))}
            </Tabla>

            <Paginador
              pagina={datos.pagina}
              total={datos.total}
              porPagina={datos.porPagina}
              ruta={rutaListado}
            />
          </>
        ) : null}
      </div>

      <ModalCotizacion
        id={abierta}
        onCerrar={cerrar}
        onEliminada={(cotizacion) => {
          setAviso(`Se eliminó la cotización ${cotizacion.numero}.`);
          cerrar();
          setVersion((valor) => valor + 1);
        }}
      />
    </>
  );
}
